import { toast } from '@heidi/design-system/toast';
import { getDefaultStore, useAtomValue } from 'jotai';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import {
  isSilentTurnAbandonment,
  type TurnOperation,
} from '../../../contracts/turn-identity';
import { getSubmitTurnErrorMessage } from '../../../data/projects/components/ProjectChat.helpers';
import { isCreditAllowanceExhausted } from '../../../data/projects/credit-allowance-exhausted';
import {
  pendingFirstTurnAtom,
  seededFirstTurnProjectIds,
  type PendingFirstTurn,
} from '../../../data/projects/pendingFirstTurn';
import type { SubmitProjectTurnArgs } from '../../../data/projects/submit-project-turn-args';

// The pure linked-context module, not the link button, so this hook doesn't pull
// the button's heavy transitive deps into the transcript import graph — that
// caused a circular-init crash when the test suite loaded.
import { stripLinkedContextPreamble } from '../../../contracts/projects/linked-context';

const autoSentProjectIds = new Set<string>();
const seededSubmitFailedProjectIds = new Set<string>();

/** Test-only: reset module-level first-turn guards between Jest cases. */
export const __resetProjectSeededFirstTurnForTests = (): void => {
  autoSentProjectIds.clear();
  seededSubmitFailedProjectIds.clear();
};

/** Allow an explicit follow-up hand-off to submit into an existing project. */
export const allowProjectSeededTurnRetry = (projectId: string): void => {
  autoSentProjectIds.delete(projectId);
  seededSubmitFailedProjectIds.delete(projectId);
};

interface UseProjectSeededFirstTurnArgs {
  projectId: string;
  agentRunId?: string;
  itemsLength: number;
  runFailed: boolean;
  runClosed: boolean;
  enqueueTurn: (command: SubmitProjectTurnArgs) => TurnOperation;
  onSubmitStart?: (
    displayText: string,
    optimisticAttachments?: PendingFirstTurn['optimisticAttachments']
  ) => void;
  onSubmitRejected?: (restoreDraftText?: string) => void;
  /**
   * The send was refused for an exhausted credit allowance: the app keeps the
   * refused text as the timeline's ghost. Pass a stable function.
   */
  onCreditAllowanceRefused: (refusal: { projectId: string; ghostText: string }) => void;
}

export const getIsStartingSeededTurn = ({
  projectId,
  itemsLength,
  runFailed,
  runClosed,
}: Pick<
  UseProjectSeededFirstTurnArgs,
  'projectId' | 'itemsLength' | 'runFailed' | 'runClosed'
>): boolean =>
  seededFirstTurnProjectIds.has(projectId) &&
  !autoSentProjectIds.has(projectId) &&
  itemsLength === 0 &&
  !runFailed &&
  !runClosed;

export const useProjectSeededFirstTurn = ({
  projectId,
  agentRunId,
  itemsLength,
  runFailed,
  runClosed,
  enqueueTurn,
  onSubmitStart,
  onSubmitRejected,
  onCreditAllowanceRefused,
}: UseProjectSeededFirstTurnArgs) => {
  const pending = useAtomValue(pendingFirstTurnAtom, { store: getDefaultStore() });
  const intl = useIntl();

  useEffect(() => {
    if (itemsLength > 0 || runFailed || runClosed) {
      seededFirstTurnProjectIds.delete(projectId);
    }
  }, [projectId, itemsLength, runFailed, runClosed]);

  useEffect(() => {
    if (autoSentProjectIds.has(projectId)) return;
    if (seededSubmitFailedProjectIds.has(projectId)) return;
    if (!agentRunId) return;
    if (!pending || pending.projectId !== projectId) return;

    const prompt = pending.prompt.trim();
    if (
      !prompt &&
      !pending.computerUseSkill &&
      !pending.attachment_ids?.length &&
      pending.files.length === 0
    ) {
      return;
    }

    autoSentProjectIds.add(projectId);
    // One trimmed display value drives both the local preview bubble and the
    // optimistic harness bubble, so the two never differ by whitespace when
    // the preview is swapped for the real message.
    const displayText = (pending.displayPrompt ?? prompt).trim();
    const restoreDraftText = pending.linked
      ? stripLinkedContextPreamble(displayText, pending.linked)
      : displayText;
    if (displayText || pending.optimisticAttachments?.length) {
      onSubmitStart?.(displayText, pending.optimisticAttachments);
    }
    getDefaultStore().set(pendingFirstTurnAtom, {
      ...pending,
      prompt: '',
      files: [],
      attachment_ids: [],
    });
    void enqueueTurn({
      text: prompt,
      displayText,
      failureUxOwner: 'caller',
      files: pending.files,
      ...(pending.attachment_ids?.length
        ? { attachment_ids: pending.attachment_ids }
        : {}),
      ...(pending.optimisticAttachments?.length
        ? { optimisticAttachments: pending.optimisticAttachments }
        : {}),
      computerUseSkill: pending.computerUseSkill,
      desktopTargetOverride: pending.desktopTargetOverride,
      ...(pending.approvalMode && { approvalMode: pending.approvalMode }),
      analytics: {
        surface: pending.analytics?.surface ?? 'work_home',
        work_mode: pending.analytics?.work_mode ?? 'work',
        has_slash_command: pending.analytics?.has_slash_command ?? false,
        ...(pending.analytics?.entry_point
          ? { entry_point: pending.analytics.entry_point }
          : {}),
        ...(pending.analytics?.clinical_session_id
          ? { clinical_session_id: pending.analytics.clinical_session_id }
          : {}),
      },
      // Carry the seeded linked context so a pre-output cancel restores the
      // prompt together with its patient/session chip. The draft text is the
      // preamble stripped off `displayText` (the chip re-serializes it on a
      // resend), and the composer's pristine guard recognises the still-
      // hydrated chip as belonging to this turn. Computer-use turns are opted
      // out of restore in the transport (a structured skill can't round-trip
      // a text draft), so no special-casing is needed here.
      ...(pending.linked && {
        restoreContext: {
          draft: {
            text: restoreDraftText,
            segments: [],
          },
          linkedItems: [pending.linked],
        },
      }),
    }).accepted.catch((error: unknown) => {
      if (isSilentTurnAbandonment(error)) {
        return;
      }
      if (isCreditAllowanceExhausted(error)) {
        onCreditAllowanceRefused({
          projectId,
          ghostText: displayText,
        });
        onSubmitRejected?.();
        seededFirstTurnProjectIds.delete(projectId);
        seededSubmitFailedProjectIds.add(projectId);
        return;
      }
      getDefaultStore().set(pendingFirstTurnAtom, pending);
      onSubmitRejected?.(restoreDraftText);
      toast({
        title: "Couldn't send first message",
        description: getSubmitTurnErrorMessage(error, intl),
        tone: 'destructive',
      });
      seededFirstTurnProjectIds.delete(projectId);
      autoSentProjectIds.delete(projectId);
      // Restoring `pending` re-arms this effect, so latch off the retry.
      seededSubmitFailedProjectIds.add(projectId);
    });
  }, [
    projectId,
    agentRunId,
    pending,
    enqueueTurn,
    intl,
    onSubmitStart,
    onSubmitRejected,
    onCreditAllowanceRefused,
  ]);
};
